package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.State
import androidx.compose.runtime.compositionLocalOf
import com.siriusxm.cmc.utils.LazyLoadedState
import com.siriusxm.cmc.utils.Route
import com.siriusxm.pia.ApplicationContext
import com.siriusxm.pia.ConsoleApplication
import com.siriusxm.smithy4kt.Model
import com.siriusxm.unifiedcontent.cmc.CMCService
import unifiedcontent.aggregator.Aggregator
import unifiedcontent.aggregator.ConsumerDetails
import unifiedcontent.aggregator.ProducerDetails
import io.ktor.client.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json

/**
 * The aggregator application.
 * This now delegates to GlobalAggregatorLoader for data loading, which happens
 * on app startup rather than when the user navigates to the aggregator section.
 */
class AggregatorApplication : ConsoleApplication {
    lateinit var context: ApplicationContext

    /**
     * The aggregator SDK, delegated to GlobalAggregatorLoader.
     */
    val aggregator: Aggregator
        get() = GlobalAggregatorLoader.aggregator

    /**
     * The entity model, populated after data loading completes.
     */
    val model: Model
        get() = GlobalAggregatorLoader.model
            ?: throw IllegalStateException("Model not loaded yet")

    /**
     * The CMC service API.
     */
    val cmcService: CMCService by lazy {
        CMCService(context.configuration.cmcApiUrl!!, HttpClient {
            install(ContentNegotiation) {
                json(Json {
                    isLenient = true
                    ignoreUnknownKeys = true
                })
            }
            // Smithy4kt decodes non-2xx responses into typed service errors.
            expectSuccess = false
            context.installAuth(this)
        })
    }

    private val userRolesState = LazyLoadedState {
        UserRoles(this).apply {
            load()
        }
    }

    /**
     * Reload producers into the application context.
     *
     * @param includeDisabled When true, includes disabled producers in the list.
     */
    suspend fun reloadProducers(includeDisabled: Boolean = false) {
        GlobalAggregatorLoader.value()?.let { current ->
            val sorted = aggregator.getProducers(includeDisabled)
                .sortedAlphaBy { p -> p.sortKey() }
            GlobalAggregatorLoader.update(current.copy(producers = sorted))
        }
    }

    /**
     * Reload consumers into the application context.
     *
     * @param includeDisabled When true, includes disabled consumers in the list.
     */
    suspend fun reloadConsumers(includeDisabled: Boolean = false) {
        GlobalAggregatorLoader.value()?.let { current ->
            val sorted = aggregator.getConsumers(includeDisabled)
                .sortedAlphaBy { c -> c.sortKey() }
            GlobalAggregatorLoader.update(current.copy(consumers = sorted))
        }
    }

    override suspend fun initialize(context: ApplicationContext) {
        this.context = context
        // SDK setup is now handled by GlobalAggregatorLoader, which is initialized
        // on app startup. This method just stores the context for later use.
    }

    @Composable
    fun userRoles(): State<UserRoles?> {
        return userRolesState.collect()
    }

    /**
     * Get the current aggregator service synchronously, or null if not yet loaded.
     */
    fun currentAggregatorService(): AggregatorService? {
        return GlobalAggregatorLoader.value()
    }

    /**
     * Get the aggregator service state. Delegates to GlobalAggregatorLoader.
     */
    @Composable
    fun aggregatorContext(): State<AggregatorService?> {
        return GlobalAggregatorLoader.collect()
    }

    @Composable
    override fun route(route: Route) {
        route.contentAggregator(this)
    }
}

val LocalAppState: ProvidableCompositionLocal<AggregatorApplication?> =
    compositionLocalOf { null }


/**
 * Get the aggregator application instance.
 */
@Composable
fun rememberAggregator(): AggregatorApplication {
    return LocalAppState.current!!
}

// small key helpers so the selector is trivial
private fun ProducerDetails.sortKey(): String =
    name?.takeIf(String::isNotBlank) ?: id

private fun ConsumerDetails.sortKey(): String =
    name?.takeIf(String::isNotBlank) ?: id
